import { supabase } from '../lib/supabase/client';
import { TimeSlot, AppointmentFormData } from '../types/models';

export const appointmentService = {
  // Get all active services
  async getServices() {
    return await supabase
      .from('services')
      .select('*')
      .eq('is_active', true)
      .order('name');
  },

  // Get available time slots for a service on a specific date
  async getAvailableSlots(serviceId: string, date: string) {
    // Get service duration
    const { data: service } = await supabase
      .from('services')
      .select('duration_minutes')
      .eq('id', serviceId)
      .single();

    if (!service) {
      return { data: [], error: null };
    }

    // Get existing appointments for the date
    const { data: existingAppointments } = await supabase
      .from('appointments')
      .select('appointment_time')
      .eq('service_id', serviceId)
      .eq('appointment_date', date)
      .in('status', ['pending', 'confirmed']);

    const bookedTimes = new Set(
      existingAppointments?.map(apt => apt.appointment_time) || []
    );

    // Generate time slots from 9 AM to 5 PM
    const slots: TimeSlot[] = [];
    for (let hour = 9; hour < 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push({
          slotTime: time,
          isAvailable: !bookedTimes.has(time)
        });
      }
    }

    return { data: slots, error: null };
  },

  // Create a new appointment
  async createAppointment(appointmentData: AppointmentFormData) {
    // Create the appointment
    const { data: appointment, error: appointmentError } = await supabase
      .from('appointments')
      .insert({
        service_id: appointmentData.serviceId,
        client_name: appointmentData.clientName,
        client_email: appointmentData.clientEmail,
        client_phone: appointmentData.clientPhone,
        appointment_date: appointmentData.appointmentDate,
        appointment_time: appointmentData.appointmentTime,
        notes: appointmentData.notes || null,
        status: 'pending'
      })
      .select(`
        *,
        services (
          *
        )
      `)
      .single();

    if (appointmentError) {
      return { data: null, error: appointmentError };
    }

    // Note: Email sending is handled by database triggers
    // No need to call Edge Functions directly from the client

    return { data: appointment, error: null };
  },

  // Get appointments by email
  async getAppointmentsByEmail(email: string) {
    return await supabase
      .from('appointments')
      .select(`
        *,
        services (
          *
        )
      `)
      .eq('client_email', email)
      .order('appointment_date', { ascending: true })
      .order('appointment_time', { ascending: true });
  },

  // Cancel an appointment
  async cancelAppointment(appointmentId: string, email: string) {
    return await supabase
      .from('appointments')
      .update({ status: 'cancelled' })
      .eq('id', appointmentId)
      .eq('client_email', email);
  }
};